'use client';

import { useMemo, useState } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  BodyStrong,
  EntityDrawerSection,
  EntityDrawerSections,
  MetaDefault,
  Spinner,
} from '@terra/ui-design-system';
import { ScrollIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { EndpointPreviewReturnDto } from '@terra/shared-contracts-endpoints';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import {
  useExpectedResponseObservationFilters,
  useExpectedResponseObservations,
} from '../../hooks/api/useExpectedResponseObservations';
import {
  buildExpectedResponseObservationTableRows,
  sortExpectedResponseObservationRowsByRecordedAt,
  type AuditLogEndpointFilterOption,
  type AuditLogFlowFilterDimension,
  type AuditLogFlowFilterOptions,
  type AuditLogFlowFilters,
} from '../../utils/group-expected-response-observations';
import { AuditLogFilters } from './AuditLogFilters';
import { AuditLogFlow } from './AuditLogFlow';

type ExpectedResponseObservationsSectionProps = {
  applicationId: string;
  threadId: string | undefined;
  endpoints: EndpointPreviewReturnDto[];
  enabled?: boolean;
  onNavigateToToolCall?: (toolCallId: string) => void;
};

function buildEndpointFilterOptions({
  endpointIds,
  endpointById,
}: {
  endpointIds: string[];
  endpointById: Map<string, EndpointPreviewReturnDto>;
}): AuditLogEndpointFilterOption[] {
  return endpointIds
    .map((endpointId) => {
      const endpoint = endpointById.get(endpointId);

      return {
        id: endpointId,
        label: endpoint
          ? `${endpoint.request.method} ${endpoint.path}`
          : endpointId,
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function ExpectedResponseObservationsSection({
  applicationId,
  threadId,
  endpoints,
  enabled = true,
  onNavigateToToolCall,
}: ExpectedResponseObservationsSectionProps) {
  const t = useTranslations('expectedResponseObservations');
  const dateFormatter = useDateFormatter();
  const [activeFilters, setActiveFilters] = useState<AuditLogFlowFilters>({
    endpoints: [],
    verbs: [],
    entities: [],
    descriptions: [],
  });
  const endpointById = useMemo(
    () => new Map(endpoints.map((endpoint) => [endpoint.id, endpoint])),
    [endpoints],
  );
  const { data: filterOptionsData } = useExpectedResponseObservationFilters({
    applicationId,
    threadId,
    enabled,
  });
  const {
    data,
    isLoading: isObservationsLoading,
    isError,
  } = useExpectedResponseObservations({
    applicationId,
    threadId,
    enabled,
    filters: activeFilters,
  });
  const summary = data?.summary ?? {
    full: 0,
    statusOnly: 0,
    unmatched: 0,
    endpointCount: 0,
    nonStateChangingCount: 0,
  };
  // Only block the whole section on the initial load; during refetches
  // (e.g. filter changes) keep the filters and previous rows mounted.
  const isLoading = enabled && isObservationsLoading && !data;
  const filterOptions = useMemo<AuditLogFlowFilterOptions>(() => {
    const apiFilters = filterOptionsData ?? {
      endpointIds: [],
      expectedVerbs: [],
      expectedEntities: [],
      expectedDescriptions: [],
    };

    return {
      endpoints: buildEndpointFilterOptions({
        endpointIds: apiFilters.endpointIds,
        endpointById,
      }),
      verbs: apiFilters.expectedVerbs,
      entities: apiFilters.expectedEntities,
      descriptions: apiFilters.expectedDescriptions,
    };
  }, [endpointById, filterOptionsData]);
  const tableRows = useMemo(
    () =>
      sortExpectedResponseObservationRowsByRecordedAt(
        buildExpectedResponseObservationTableRows({
          observations: data?.observations ?? [],
          endpointById,
        }),
      ),
    [data?.observations, endpointById],
  );
  const handleFilterChange = ({
    dimension,
    values,
  }: {
    dimension: AuditLogFlowFilterDimension;
    values: string[];
  }) => {
    setActiveFilters((current) => ({
      ...current,
      [dimension]: values,
    }));
  };

  const handleClearFilters = () => {
    setActiveFilters({
      endpoints: [],
      verbs: [],
      entities: [],
      descriptions: [],
    });
  };

  if (isLoading) {
    return (
      <EntityDrawerSections>
        <EntityDrawerSection title={t('title')}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={8}
          >
            <Spinner size="lg" />
          </Box>
        </EntityDrawerSection>
      </EntityDrawerSections>
    );
  }

  // Only take over the whole section on an initial-load failure. On a refetch
  // error (e.g. after changing filters) React Query keeps the last data, so we
  // fall through and keep the filters + cached rows visible.
  if (isError && !data) {
    return (
      <EntityDrawerSections>
        <EntityDrawerSection title={t('title')}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={8}
          >
            <BodyDefault color="fg.muted" textAlign="center">
              {t('error')}
            </BodyDefault>
          </Box>
        </EntityDrawerSection>
      </EntityDrawerSections>
    );
  }

  if (summary.full + summary.statusOnly + summary.unmatched === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="full"
        h="full"
        py={12}
      >
        <VStack gap={4} alignItems="center" maxW="md">
          <ScrollIcon size={24} color="var(--colors-fg-muted)" />
          <VStack gap={2} alignItems="center">
            <BodyStrong color="fg.default">{t('emptyTitle')}</BodyStrong>
            <MetaDefault color="fg.muted" textAlign="center">
              {t('empty')}
            </MetaDefault>
          </VStack>
        </VStack>
      </Box>
    );
  }

  return (
    <EntityDrawerSections>
      <EntityDrawerSection title={t('title')}>
        <VStack gap={4} alignItems="stretch" w="full">
          <AuditLogFilters
            options={filterOptions}
            filters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearFilters}
          />
          {tableRows.length > 0 ? (
            <AuditLogFlow
              rows={tableRows}
              dateFormatter={dateFormatter}
              onNavigateToToolCall={onNavigateToToolCall}
            />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="full"
              py={12}
            >
              <BodyDefault color="fg.muted" textAlign="center">
                {t('filters.noResults')}
              </BodyDefault>
            </Box>
          )}
        </VStack>
      </EntityDrawerSection>
    </EntityDrawerSections>
  );
}
